import * as React from 'react'
import { getDownloadUrl } from '@/client/sdk.gen'

export interface ArtifactDownloadUrlState {
	url: string | null
	loading: boolean
	error: boolean
}

const INELIGIBLE: ArtifactDownloadUrlState = {
	url: null,
	loading: false,
	error: false,
}

/**
 * Fetches the presigned download URL for an artifact.
 * Only fetches when artifact status is 'completed'.
 */
export function useArtifactDownloadUrl(
	projectId: string | undefined,
	artifactId: string | null | undefined,
	status: string
): ArtifactDownloadUrlState {
	const [state, setState] = React.useState<ArtifactDownloadUrlState>(() =>
		status === 'completed' && projectId && artifactId
			? { url: null, loading: true, error: false }
			: INELIGIBLE
	)

	React.useEffect(() => {
		if (status !== 'completed' || !projectId || !artifactId) {
			setState(INELIGIBLE)
			return
		}

		let cancelled = false
		setState({ url: null, loading: true, error: false })

		getDownloadUrl({
			path: { project_id: projectId, artifact_id: artifactId },
		})
			.then((response) => {
				if (cancelled) return
				if (response.data?.download_url) {
					setState({ url: response.data.download_url, loading: false, error: false })
				} else {
					setState({ url: null, loading: false, error: true })
				}
			})
			.catch(() => {
				if (!cancelled) {
					setState({ url: null, loading: false, error: true })
				}
			})

		return () => {
			cancelled = true
		}
	}, [projectId, artifactId, status])

	return state
}
